import { Component, inject } from '@angular/core';
import {
  AccountCreationResponses,
  FinancialAccountsCreationService,
  SocureService,
} from '@brightside-web/desktop/feature/financial-accounts-core';
import { Environment } from '@micro-core/environment';
import { GenericResponseCode, SimpleGlobalModalService } from '@brightside-web/desktop/data-access/core-services';
import { take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseService } from '@brightside-web/desktop/data-access/shared';

interface SocureConfig {
  onSuccess?: (response: { documentUuid: string }) => void;
  onError?: (error: SocureVerificationEvent | null) => void;
  onProgress?: (event: unknown) => void;
  qrCodeNeeded?: boolean;
  disableSmsInput?: boolean;
  [key: string]: any;
}

interface SocureVerificationEvent {
  eventId: string;
  status: string;
  verificationLevel: number;
  mobileNumber: string;
  key: string;
  referenceId: string;
  verifyResult: {
    referenceId: string;
  };
  documentUuid: string;
}

interface SocureDocVSDK {
  launch(
    sdkKey: string,
    docvTransactionToken: string,
    divSelector: string,
    config?: SocureConfig
  ): void;
}

declare let SocureDocVSDK: SocureDocVSDK;

@Component({
    selector: 'brightside-web-financial-core-kyc',
    templateUrl: './socure.component.html',
    styleUrls: ['./socure.component.scss'],
    standalone: false
})
export class FinancialCoreKYCComponent {
  showSpinner = true;
  showVerifyingModal = false;

  socureScript: HTMLScriptElement;
  sdkInitiated: boolean;
  showKycModal: boolean;

  /** Time to wait for Socure SDK to render iframe before treating as silent failure (invalid token, etc.). */
  private readonly _socureLoadTimeoutMs = 6000;
  private readonly _socureLoadCheckIntervalMs = 500;
  private _socureLoadCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _socureLoadCheckIntervalId: ReturnType<typeof setInterval> | null = null;

  config: SocureConfig = {
    onProgress: this.onProgress.bind(this),
    onSuccess: this.onSuccess.bind(this),
    onError: this.onError.bind(this),
    qrCodeNeeded: true,
  };

  private financialCoreSvc: FinancialAccountsCreationService = inject(FinancialAccountsCreationService);
  private socureSvc: SocureService = inject(SocureService);

  constructor(
    private env: Environment,
    private simpleModalSvc: SimpleGlobalModalService,
    private router: Router,
    private route: ActivatedRoute,
    private analytics: FirebaseService
  ) {
    if (!this.route.snapshot.data['accountType']) {
      console.warn('This component should have an accountType in the ActivatedRouteSnapshot data');
    } else {
      this.financialCoreSvc.setAccountType(this.route.snapshot.data['accountType']);
    }
  }

  onProgress(progress: unknown) {
    this._clearSocureLoadCheckTimeout();
  }

  onSuccess(response: { documentUuid: string }) {
    this._clearSocureLoadCheckTimeout();
    this.showVerifying();
    this.financialCoreSvc
      .kycCheck(response.documentUuid)
      .pipe(take(1))
      .subscribe((kycResp) => {
        this.showVerifyingModal = false;
        if (kycResp.result.code === AccountCreationResponses.KYC_VERIFIED) {
          this.router.navigate(['accounts', this.financialCoreSvc.accountType, 'dashboard']);
        }
      });
  }

  onError(error: SocureVerificationEvent | null) {
    this._clearSocureLoadCheckTimeout();
    this.showKycModal = false;
    const errorString = error ? error.status : 'unknown';
    this.analytics.logEvent('kyc_failed', { code: errorString });
    this.simpleModalSvc.createGenericErrorModal('kyc_automated_review', () => {
      this.showSpinner = true;
      this.showSocureModal();
    });
  }

  showSocureModal() {
    this._clearSocureLoadCheckTimeout();
    this.showKycModal = true;
    setTimeout(() => {
      this.socureScript = this.socureSvc.loadSocureScript();
      this.socureScript.onload = () => {
        this.clearSession();
        
        // Get the docvTransactionToken from the backend
        this.socureSvc
          .getDocvTransactionToken()
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              if (response.result?.code !== GenericResponseCode.SERVICE_OK) {
                this.showKycModal = false;
                this.showSpinner = false;
                this.onError(null);
                return;
              }
              const token = response.payload.docvTransactionToken;

              if (typeof window !== 'undefined' && (window as any).SocureDocVSDK) {
                try {
                  SocureDocVSDK.launch(
                    this.env.socure,
                    token,
                    '#socureDiv',
                    this.config
                  );
                  this.showSpinner = false;
                  this.sdkInitiated = true;
                  this._startSocureLoadCheckTimeout();
                } catch (error) {
                  console.error('Error launching SocureDocVSDK:', error);
                  this.onError(null);
                }
              } else {
                console.error('SocureDocVSDK not available');
                this.onError(null);
              }
            },
            error: (error) => {
              console.error('Error getting docvTransactionToken:', error);
              this.onError(null);
            }
          });
      };
    }, 10);
  }

  clearSession() {
    // Clean up session storage
    sessionStorage.removeItem('documentVerificationToken');
    sessionStorage.removeItem('publicApiKey');
    localStorage.removeItem('devicer_id');
  }

  private _clearSocureLoadCheckTimeout(): void {
    if (this._socureLoadCheckTimeoutId != null) {
      clearTimeout(this._socureLoadCheckTimeoutId);
      this._socureLoadCheckTimeoutId = null;
    }
    if (this._socureLoadCheckIntervalId != null) {
      clearInterval(this._socureLoadCheckIntervalId);
      this._socureLoadCheckIntervalId = null;
    }
  }

  /**
   * Socure SDK does not call onError when the token is invalid; it never injects content.
   * Callbacks only fire after the user interacts on their phone, so we poll for injected content in #socureDiv.
   * When content appears we clear the timeout; if it never appears by the timeout, show error + try again.
   */
  private _startSocureLoadCheckTimeout(): void {
    this._clearSocureLoadCheckTimeout();
    this._socureLoadCheckTimeoutId = setTimeout(() => {
      this._clearSocureLoadCheckTimeout();
      if (this.showKycModal) {
        this.showKycModal = false;
        this.showSpinner = false;
        this.onError(null);
      }
    }, this._socureLoadTimeoutMs);

    this._socureLoadCheckIntervalId = setInterval(() => {
      const socureDiv = document.getElementById('socureDiv');
      const hasContent = (socureDiv?.childNodes?.length ?? 0) > 0;
      if (hasContent) {
        this._clearSocureLoadCheckTimeout();
      }
    }, this._socureLoadCheckIntervalMs);
  }

  private showVerifying() {
    this.showVerifyingModal = true;
    this.showKycModal = false;
    this.showSpinner = true;
    this.clearSession();
  }

  goHome() {
    this.router.navigate(['home']);
  }
}
